import { Component, forwardRef, inject, Input } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ControlValueAccessorDirective } from '@sharedModule/directives/control-value-accessor.directive';
import { InputData } from '@sharedModule/models/input-data.model';
import { ImageCropperDialogComponent } from './image-cropper-dialog/image-cropper-dialog.component';

/**
 * Reusable image upload + crop control. Selecting a file opens a crop popup;
 * the finalized crop is stored on the bound form control as a base64 data URL,
 * so it plugs into reactive forms via formControlName.
 */
@Component({
  selector: 'app-input-image-crop',
  templateUrl: './input-image-crop.component.html',
  styleUrl: './input-image-crop.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputImageCropComponent),
      multi: true,
    },
  ],
})
export class InputImageCropComponent extends ControlValueAccessorDirective<string> {
  @Input() inputData?: InputData;
  @Input() hint?: string;
  @Input() accept = 'image/png,image/jpeg,image/webp,image/svg+xml';

  private readonly dialog = inject(MatDialog);
  fileName = '';

  /** Current cropped image (base64), read straight from the bound control. */
  get imageValue(): string {
    return this.control?.value ?? '';
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    this.dialog
      .open(ImageCropperDialogComponent, {
        disableClose: true,
        width: '600px',
        data: { file },
      })
      .afterClosed()
      .subscribe((croppedImage?: string) => {
        if (croppedImage) {
          this.fileName = file.name;
          this.control?.setValue(croppedImage);
        }
        // Reset so re-selecting the same file fires (change) again.
        input.value = '';
      });
  }

  removeImage(): void {
    this.fileName = '';
    this.control?.setValue('');
  }
}
